import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { getAccounts, type Account } from '@/features/accounts/accounts.api';

/** React Query key for the accounts list. Exported so the WebSocket layer
 *  (M5-T6) can invalidate / `setQueryData` against it. */
export const accountsQueryKey = ['accounts'] as const;

/**
 * React Query hook over `GET /api/v1/accounts`.
 *
 *   - `staleTime: 15s` — account rows change infrequently (status flips on
 *     QR re-pair, disconnect, reconnect); 15s feels live without spamming
 *     the Hub.
 *   - `refetchInterval: 30s` — keeps the status dots roughly current without
 *     the WebSocket. TODO(M5-T6): switch to WS-driven `setQueryData` from
 *     `account.status` events and drop the polling.
 *   - `retry: 1` — one quiet retry on a transient blip.
 */
export function useAccounts(): UseQueryResult<Account[], Error> {
  return useQuery<Account[], Error>({
    queryKey: accountsQueryKey,
    queryFn: getAccounts,
    staleTime: 15_000,
    // TODO(M5-T6): once WebSocket `account.status` events are wired, drive the
    // cache via `queryClient.setQueryData` and reduce or remove this polling.
    refetchInterval: 30_000,
    retry: 1,
  });
}
